import {
  Component,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  AfterContentInit,
  ContentChild,
  TemplateRef,
  signal,
  computed,
  input,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TableCellDirective } from './directives/table-cell.directive';
import { TableExpandableContentDirective } from './directives/table-expandable-content.directive';
import { ColumnConfig, TableConfig, SortEvent } from './interfaces/table-config.interface';
import { EllipsisLoadingComponent } from '../ellipsis-loading/ellipsis-loading.component';
import { PaginatorComponent } from '../paginator/paginator.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { IconComponent } from 'src/app/components/icon/icon.component';

@Component({
  selector: 'app-generic-table',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    EllipsisLoadingComponent,
    PaginatorComponent,
    IconComponent,
  ],
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0px', opacity: 0 })),
      ]),
    ]),
  ],
})
export class GenericTableComponent<
  T,
  TCustomFields extends string = string,
> implements AfterContentInit {
  readonly id = input<string | undefined>(undefined);

  data = input<T[]>([]);
  config = input.required<TableConfig<T, TCustomFields>>();
  isLoading = input(false);
  noDataMessage = input($localize`:@@noRecordsFound:No se han encontrado registros.`);
  showPagination = input(true);
  page = input(0);
  pageSize = input(10);
  totalRegisters = input(0);

  @Output() sortChange = new EventEmitter<SortEvent<T, TCustomFields>>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() rowExpanded = new EventEmitter<{ row: T; index: number }>();

  @ContentChildren(TableCellDirective) cellTemplates!: QueryList<TableCellDirective>;
  @ContentChild('noDataContent', { read: TemplateRef }) noDataContentTemplate?: TemplateRef<any>;
  @ContentChild(TableExpandableContentDirective)
  expandableContentTemplate?: TableExpandableContentDirective;

  private readonly templateMap = signal(new Map<string, TableCellDirective>());
  private readonly expandedRowsSet = signal(new Set<number>());

  constructor() {
    effect(() => {
      if (this.isLoading()) {
        this.expandedRowsSet.set(new Set());
      }
    });
  }

  gridTemplate = computed(() => {
    const config = this.config();
    const expandColumn = this.hasExpandableContent() ? ' 48px' : '';
    const columns = config.columns
      .map((col) => {
        if (col.width) return col.width;
        if (col.minWidth && col.maxWidth) {
          return `minmax(${col.minWidth}, ${col.maxWidth})`;
        }
        if (col.minWidth) return `minmax(${col.minWidth}, 1fr)`;
        if (col.maxWidth) return col.maxWidth;
        return '1fr';
      })
      .join(' ');
    return columns + expandColumn;
  });

  columnStyleMap = computed(() => {
    const styles: Record<string, string> = {};

    this.config().columns.forEach((col) => {
      const columnId = String(col.id);

      styles[columnId] = `
      min-width: ${col.minWidth || '0px'};
      max-width: ${col.maxWidth || 'none'};
      justify-content: ${this.getJustifyContent(col.textAlign)};
      text-align: ${col.textAlign || 'left'};
    `;
    });

    return styles;
  });

  private getJustifyContent(textAlign?: string): string {
    if (textAlign === 'right') {
      return 'flex-end';
    }
    if (textAlign === 'center') {
      return 'center';
    }
    return 'flex-start';
  }

  ngAfterContentInit(): void {
    this.updateTemplateMap();
    this.cellTemplates.changes.subscribe(() => this.updateTemplateMap());
  }

  trackByItem = (index: number, item: T): any => {
    const trackProps = this.config().trackByProp;
    if (trackProps && Array.isArray(trackProps) && trackProps.length > 0) {
      return trackProps.map((p) => (item as any)[p]).join('|');
    }
    return (item as any).id ?? (item as any).uuid ?? index;
  };

  isRowExpanded(index: number): boolean {
    return this.expandedRowsSet().has(index);
  }

  toggleRowExpansion(index: number): void {
    if (!this.config().expandableRows) return;

    const wasExpanded = this.expandedRowsSet().has(index);

    this.expandedRowsSet.update((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else {
        if (this.config().singleExpansion) next.clear();
        next.add(index);
      }
      return next;
    });

    if (!wasExpanded) {
      const row = this.data()[index];
      this.rowExpanded.emit({ row, index });
    }
  }

  onColumnHeaderClick(column: ColumnConfig<T, TCustomFields>): void {
    if (!column.sortable) return;
    const newOrder = column.sortOrder === 'asc' ? 'desc' : 'asc';

    this.config().columns.forEach((col) => (col.sortOrder = col === column ? newOrder : null));

    this.sortChange.emit({
      column,
      order: newOrder,
      sortField: column.sortField || String(column.id),
    });
  }

  onPageChange(newPage: number): void {
    this.expandedRowsSet.set(new Set());
    this.pageChange.emit(newPage);
  }

  hasExpandableContent = () => !!(this.config().expandableRows && this.expandableContentTemplate);
  getTemplate = (id: string) => this.templateMap().get(id);
  getSortIcon(col: any): string {
    if (col.sortOrder === 'asc') {
      return 'arrow_upward';
    } else if (col.sortOrder === 'desc') {
      return 'arrow_downward';
    } else {
      return 'unfold_more';
    }
  }
  getCellValue = (item: any, col: any) =>
    col.getValue ? col.getValue(item) : (item[col.id] ?? '');

  getRowId(item: T, index: number): string {
    const config = this.config();
    if (config.rowIdFn) {
      return config.rowIdFn(item);
    }
    return `idx-${index}`;
  }

  private updateTemplateMap() {
    const newMap = new Map<string, TableCellDirective>();
    this.cellTemplates.forEach((t) => newMap.set(t.columnId, t));
    this.templateMap.set(newMap);
  }
}
